import { FC, FormEventHandler, useEffect, useState } from "react";
import styles from "./add-pilot.module.scss";
import { Modal } from "../../../../../ui/modal/Modal";
import { Button } from "../../../../../ui/button/Button";
import { z } from "zod";
import { Snackbar } from "../../../../../ui/snackbar/Snackbar";
import { useAddPilotMutation } from "../pilotsApi";
import { useGetAllPersonsQuery } from "../../airStaff/airStaffApi";
import Checkbox from "../../../../../ui/Checkbox/Checkbox";

type Props = {
  close: () => void;
  flight_work_id: number;
};

type State = {
  errors?: {
    worker_id?: string[];
    inspector?: string[];
    intern?: string[];
  };
  message?: string | null;
};

const FormSchema = z.object({
  worker_id: z.coerce.number().positive({
    message: "Выберите из списка",
  }),
  inspector: z.coerce.boolean(),
  intern: z.coerce.boolean(),
});

const Form: FC<Props> = ({ close, flight_work_id }) => {
  const initialState: State = { message: null, errors: {} };
  const [state, setState] = useState<State>(initialState);
  const [isUXOpen, setIsUXOpen] = useState(true);
  const [add, { isError, isLoading, isSuccess }] = useAddPilotMutation();
  const {
    data: airStaff,
    isError: isAirstaffError,
    isFetching: isAirstaffFetching,
  } = useGetAllPersonsQuery();

  const submit: FormEventHandler = (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    setState(initialState);
    setIsUXOpen(true);

    const validatedFields = FormSchema.safeParse({
      worker_id: formData.get("worker_id"),
      inspector: formData.get("inspector"),
      intern: formData.get("intern"),
    });

    if (!validatedFields.success) {
      setState({
        errors: validatedFields.error.flatten().fieldErrors,
        message: "Не удалось сохранить форму. Заполните все поля.",
      });
      return;
    }

    const { worker_id, inspector, intern } = validatedFields.data;

    const getRole = () => {
      if (inspector) return "inspector";
      if (intern) return "intern";
      return "pilot";
    };

    add({
      flight_work_id,
      worker_id,
      role: getRole(),
    });
  };

  useEffect(() => {
    if (!isSuccess) return;
    setTimeout(() => {
      close();
    }, 1500);
  }, [isSuccess]);

  return (
    <form className={styles.form} onSubmit={submit}>
      <div className={styles.title}>Добавление летчика-наблюдателя</div>
      <div className={styles.row}>
        <label htmlFor="worker_id" className={styles.label}>
          Летчик-наблюдатель
        </label>
        <select className={styles.select} name="worker_id">
          {isAirstaffFetching ? (
            <option>Загрузка списка...</option>
          ) : (
            <option className={styles.option}>Выберите</option>
          )}
          {airStaff?.map((item) => (
            <option key={item.id} className={styles.option} value={item.id}>
              <>{`${item.number} | ${item.name}`}</>
            </option>
          ))}
        </select>
        <div id="worker_id-error" aria-live="polite" aria-atomic="true">
          {state.errors?.worker_id &&
            state.errors.worker_id.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="inspector">Проверяющий</label>
        <div className={styles.checkbox}>
          <Checkbox name="inspector" />
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="intern">Стажер</label>
        <div className={styles.checkbox}>
          <Checkbox name="intern" />
        </div>
      </div>
      <div className={styles.actions}>
        <Button type="reset" onClick={() => setState(initialState)}>
          Очистить
        </Button>
        <Button type="submit" isProcessing={isLoading} isDisabled={isLoading}>
          Сохранить
        </Button>
      </div>
      {state.message && isUXOpen && (
        <Snackbar
          isOpen={Boolean(state.message)}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          {state.message}
        </Snackbar>
      )}
      {isSuccess && (
        <Snackbar isOpen={isSuccess} className="success" onClose={close}>
          Изменения сохранены
        </Snackbar>
      )}
      {isError && isUXOpen && (
        <Snackbar
          isOpen={isError}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка сохранения. Попробуйте позже
        </Snackbar>
      )}
      {isAirstaffError && (
        <Snackbar
          isOpen={isAirstaffError}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          Не удалось загрузить список летчиков-наблюдателей
        </Snackbar>
      )}
    </form>
  );
};

export const AddPilot: FC<Props> = ({ close, flight_work_id }) => {
  return (
    <Modal close={close}>
      <Form close={close} flight_work_id={flight_work_id} />
    </Modal>
  );
};
